<template>
  <teleport to="body">
    <WdsModal
      :show="open && remainingDocs.length > 1"
      :title="$t('Review contract documents')"
      lg
      @cancel="close"
    >
      <WdsStack direction-column gap-md>
        <template v-if="loading">
          <div class="tw-py-4 tw-flex tw-justify-center">
            <WdsSpinner indeterminate :size="36" />
          </div>
        </template>

        <template v-else-if="remainingDocs.length === 0">
          <WdsText>
            {{
              $t(
                'No pending contract documents were found. If this seems wrong, refresh the page and try again.',
              )
            }}
          </WdsText>
        </template>

        <template v-else>
          <WdsText>
            {{
              $t(
                'Please review and accept each contract document before continuing with this hire.',
              )
            }}
          </WdsText>

          <WdsStack direction-column gap-sm>
            <WdsStack
              v-for="doc in remainingDocs"
              :key="doc.id"
              justify-space-between
              align-center
              class="tw-border tw-border-gray-200 tw-rounded tw-p-3"
            >
              <WdsText>
                <strong>{{ doc.docType }}</strong>
              </WdsText>
              <WdsButton primary @click="selectedDoc = doc">
                {{ $t('Review and accept') }}
              </WdsButton>
            </WdsStack>
          </WdsStack>
        </template>
      </WdsStack>

      <template #footer>
        <WdsStack justify-end class="tw-w-full">
          <WdsButton @click="close">{{ $t('Close') }}</WdsButton>
        </WdsStack>
      </template>
    </WdsModal>

    <WdsModal
      lg
      :show="docToAccept !== null && open"
      :title="
        $t('Confirm acceptance of :documentName', {
          documentName: docToAccept?.docType ?? 'Document',
        })
      "
      class="contract-doc-review-modal"
      @cancel="closeAcceptance"
    >
      <template v-if="docToAccept !== null" #default>
        <div class="tw-w-full tw-overflow-hidden tw-h-[calc(100vh-350px)] tw-max-h-[600px]">
          <iframe
            v-if="docToAccept.pdfUrl !== null"
            :src="docToAccept.pdfUrl"
            class="tw-w-full tw-h-full tw-border-0"
            title="Contract document PDF"
          />
        </div>
        <WdsStack align-center class="tw-mt-2">
          <WdsText sm>{{ $t('Having trouble viewing?') }}</WdsText>
          <WdsButtonGhost secondary xs :href="docToAccept.pdfUrl" target="_blank">
            {{ $t('Download document') }}
          </WdsButtonGhost>
        </WdsStack>
      </template>

      <template #footer>
        <WdsStack justify-space-between class="tw-w-full">
          <WdsButton @click="closeAcceptance">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsButton
            primary
            data-test="confirm-contract-doc-acceptance-button"
            :disabled="accepting"
            @click="confirmAcceptance"
          >
            {{ accepting ? $t('Confirming...') : $t('Confirm acceptance') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import { useToast } from '@/composables/useToast'
import { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'
import { graphql, useFragment } from '@/gql'
import { ContractDocAcceptanceFragment } from '@/gql/graphql'

const props = withDefaults(defineProps<ComplianceModalProps>(), {
  open: false,
  hideFooter: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const toast = useToast()
const selectedDoc = ref<ContractDocAcceptanceFragment | null>(null)
const accepting = ref(false)
const acceptedDocIds = ref<Set<string>>(new Set())

const close = () => emit('update:open', false)

const ContractDocAcceptance = graphql(`
  fragment ContractDocAcceptance on ContractDocument {
    id
    docType
    pdfUrl
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query ContractDocModalQuery($id: ID!) {
      hire(id: $id) {
        id
        latestContract {
          id
          contractDocuments {
            ...ContractDocAcceptance
          }
        }
      }
    }
  `),
  () => ({
    id: props.hireId,
  }),
  {
    clientId: 'publicApi',
  },
)

const docs = computed<ContractDocAcceptanceFragment[]>(
  () =>
    result.value?.hire?.latestContract?.contractDocuments.map((doc) =>
      useFragment(ContractDocAcceptance, doc),
    ) ?? [],
)

const remainingDocs = computed(() => docs.value.filter((doc) => !acceptedDocIds.value.has(doc.id)))

const docToAccept = computed(() => {
  if (remainingDocs.value.length === 1) {
    return remainingDocs.value[0]
  }
  return selectedDoc.value
})

const closeAcceptance = () => {
  if (remainingDocs.value.length === 1) {
    close()
  } else {
    selectedDoc.value = null
  }
}

const getCsrfToken = (): string | null =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? null

const confirmAcceptance = async () => {
  if (docToAccept.value === null) {
    return
  }

  const csrfToken = getCsrfToken()
  if (!csrfToken) {
    toast.error($t('Unable to submit. Please refresh and try again.'))
    return
  }

  accepting.value = true

  try {
    const formAction = laravelNamedRoute('contract-doc.accept', {
      contractDoc: docToAccept.value.id,
    })

    const response = await fetch(formAction, {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': csrfToken,
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      credentials: 'same-origin',
      body: new URLSearchParams(),
    })

    if (!response.ok) {
      throw new Error(`Unexpected response ${response.status}`)
    }

    acceptedDocIds.value = new Set([...acceptedDocIds.value, docToAccept.value.id])
    selectedDoc.value = null

    await publicApi.refetchQueries({
      include: queriesToRefetch,
    })

    if (remainingDocs.value.length === 0) {
      close()
      toast.success($t('All contract documents have been accepted'))
    } else {
      toast.success($t('Document accepted'))
    }
  } catch {
    toast.error(
      $t(
        'An error occurred while confirming acceptance. Please try again, or reach out to support.',
      ),
    )
  } finally {
    accepting.value = false
  }
}
</script>

<style scoped>
.contract-doc-review-modal :deep(.wds-modal-content) {
  width: 90%;
  max-width: 720px;
}

@media (min-width: 768px) {
  .contract-doc-review-modal :deep(.wds-modal-content) {
    width: 70%;
  }
}
</style>
